import type { DayOfWeek } from '@gozoki/api-types'

export type ReapproComputeAlgorithm = 'VMAX' | 'VMJ' | 'STOCK_PROJETE' | 'STOCK_MINIMUM'
export type ReaproPeriode = '3J' | '5J' | '7J'

export interface ReapproRealtimeIndicators {
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  vma3j: number
  vma5j: number
  vma7j: number
  vjm3j: number
  vjm5j: number
  vjm7j: number
}

export const EMPTY_REALTIME_INDICATORS: ReapproRealtimeIndicators = {
  stockActuel: 0,
  stockDLCCourte: 0,
  stockArrivant: 0,
  vma3j: 0,
  vma5j: 0,
  vma7j: 0,
  vjm3j: 0,
  vjm5j: 0,
  vjm7j: 0,
}

export const sanitizeNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const pickIndicatorByPeriode = (
  realtime: ReapproRealtimeIndicators | undefined,
  periode: ReaproPeriode,
): { vmaxBase: number; vmjBase: number } => {
  if (!realtime) {
    return { vmaxBase: 0, vmjBase: 0 }
  }
  if (periode === '3J') {
    return { vmaxBase: realtime.vma3j, vmjBase: realtime.vjm3j }
  }
  if (periode === '5J') {
    return { vmaxBase: realtime.vma5j, vmjBase: realtime.vjm5j }
  }
  return { vmaxBase: realtime.vma7j, vmjBase: realtime.vjm7j }
}

/**
 * Returns true if any of the coverage days (starting from deliveryDayOfWeek)
 * include a Saturday or Sunday.
 * @param deliveryDayOfWeek 0=Sunday, 1=Monday, ..., 6=Saturday (JS Date.getDay() convention)
 * @param coverageRate number of days to cover (ceiled to integer)
 */
export const getReelWeekEndCoef = (coveringDays: DayOfWeek[], initialCoef: number): number => {
  const isThereAWeekendInCoveringDays = coveringDays.some((day) =>
    ['saturday', 'sunday'].includes(day),
  )
  return isThereAWeekendInCoveringDays ? initialCoef : 1
}

interface ComputeProductNeedInput {
  algorithm: ReapproComputeAlgorithm
  coverageRate: number
  coefSecu: number
  coefWeekend: number
  stockMinimum: number | null
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  vmaxBase: number
  vmjBase: number
  daysBefore?: number
  /** 0=Sunday, 1=Monday, ..., 6=Saturday. When provided, coefWeekend only applies if coverage includes a weekend day. */
  deliveryDayOfWeek?: number
  /** VMJ day-of-week weight for the production day (J). Defaults to 1. */
  poidsJourJ?: number
  /** VMJ day-of-week weight for the delivery day (J+1). Defaults to 1. */
  poidsJourJ1?: number
}

export interface ComputeProductNeedResult {
  besoin: number
  vmax: number
  vmj: number
  prevision: number
  stockProjete: number
  vmaxSubstitution: number
  vmjSubstitution: number
}

export const roundUpToPcb = (value: number, pcb: number): number => {
  if (pcb <= 0 || value <= 0) return value
  return Math.ceil(value / pcb) * pcb
}

export interface AllocationInput {
  storeId: string
  besoin: number // raw need (before PCB rounding)
  cappedNeed: number // need rounded up to nearest PCB multiple
  colisage: number
}

export const computeDispatchAllocations = (
  inputs: AllocationInput[],
  platformStock: number,
): Record<string, number> => {
  const totalCapped = inputs.reduce((sum, i) => sum + i.cappedNeed, 0)

  if (totalCapped <= 0 || platformStock <= 0) {
    return Object.fromEntries(inputs.map((i) => [i.storeId, 0]))
  }

  if (platformStock >= totalCapped) {
    return Object.fromEntries(inputs.map((i) => [i.storeId, i.cappedNeed]))
  }

  const totalBesoin = inputs.reduce((sum, i) => sum + i.besoin, 0)

  const isCase3 = platformStock <= totalBesoin
  const ratio = isCase3 ? platformStock / totalBesoin : platformStock / totalCapped

  const items = inputs.map((i) => {
    const base = isCase3 ? i.besoin : i.cappedNeed
    const rawAlloc = base * ratio
    const floored = Math.floor(rawAlloc / i.colisage) * i.colisage
    const remainder = rawAlloc % i.colisage
    return { storeId: i.storeId, floored, remainder, colisage: i.colisage }
  })

  const totalFloored = items.reduce((sum, i) => sum + i.floored, 0)
  let remainingStock = Math.round(platformStock - totalFloored)

  const sorted = [...items].sort((a, b) => b.remainder - a.remainder)
  const result: Record<string, number> = Object.fromEntries(
    items.map((i) => [i.storeId, i.floored]),
  )

  for (const item of sorted) {
    if (remainingStock < item.colisage) continue
    result[item.storeId] += item.colisage
    remainingStock -= item.colisage
  }

  return result
}

export const computeProductNeed = ({
  algorithm,
  coverageRate,
  coefSecu,
  coefWeekend,
  stockMinimum,
  stockActuel,
  stockDLCCourte,
  stockArrivant,
  vmaxBase,
  vmjBase,
  daysBefore = 0,
  poidsJourJ: rawPoidsJourJ,
  poidsJourJ1: rawPoidsJourJ1,
}: ComputeProductNeedInput): ComputeProductNeedResult => {
  const sa = sanitizeNumber(stockActuel)
  const dlc = sanitizeNumber(stockDLCCourte)
  const arr = sanitizeNumber(stockArrivant)
  const db = Math.max(0, sanitizeNumber(daysBefore))
  const tc = Math.max(0, sanitizeNumber(coverageRate))
  const cs = Math.max(0, sanitizeNumber(coefSecu))
  const rawCw = Math.max(0, sanitizeNumber(coefWeekend))
  const cw = rawCw ?? 1
  const vmaxSignal = Math.max(0, sanitizeNumber(vmaxBase))
  const vmjSignal = Math.max(0, sanitizeNumber(vmjBase))
  const pJ = rawPoidsJourJ ?? 1
  const pJ1 = rawPoidsJourJ1 ?? 1
  const stockProjete = Math.max(0, Math.ceil(sa - Math.max(vmjSignal * db, dlc) + arr))
  const prevision = vmjSignal * tc
  const vmaxSubstitution = vmaxSignal * tc - sa + dlc - arr
  const vmjSubstitution = vmjSignal * pJ1 * cs + tc * vmjSignal * pJ - (sa - dlc + arr)

  if (algorithm === 'STOCK_MINIMUM') {
    const customStockMin = sanitizeNumber(stockMinimum)
    const needFromCustomStockMin = customStockMin - (sa - dlc + arr)
    return {
      besoin: Math.max(0, Math.round(needFromCustomStockMin)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  if (algorithm === 'STOCK_PROJETE') {
    return {
      besoin: Math.max(0, Math.round(vmaxSignal * cs * tc - stockProjete)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  if (algorithm === 'VMAX') {
    return {
      besoin: Math.max(0, Math.round(vmaxSubstitution * cs * cw)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  return {
    besoin: Math.max(0, Math.round(vmjSubstitution)),
    vmax: vmaxSignal,
    vmj: vmjSignal,
    prevision,
    stockProjete,
    vmaxSubstitution,
    vmjSubstitution,
  }
}

// Round a need up to the nearest multiple of colisage (units per case).
// Returns the need unchanged if colisage <= 1 or need <= 0.
export const roundUpToColisage = (need: number, colisage: number): number => {
  if (colisage <= 1 || need <= 0) return need
  return Math.ceil(need / colisage) * colisage
}
